import type { ReactNode } from 'react';
import { LegalLinks } from '@/components/legal/LegalLinks';

/**
 * Shared shell for the AuthShell-based auth screens (Signup + 4 secondary:
 * Forgot / Reset / Verify / OAuthCallback).
 *
 * slot_ux_polish UXP.3 (2026-05-23 · Genie-approved «light sync»): the Buddy
 * mascot placeholder was removed and the shell adopts the editorial palette of
 * the redesigned Login — cream hero panel · dark form panel · mono labels ·
 * underline inputs (styling lives in globals.css · class names preserved).
 * The Login screen itself no longer uses this shell — it has a bespoke layout.
 *
 * Desktop ≥768px: 50/50 split (editorial hero left · form right).
 * Mobile <768px: stacks · hero compressed on top · form below.
 */

export interface AuthShellProps {
  /** h1 «Войти» / «Восстановить доступ» / etc. */
  title: ReactNode;
  /** .subtitle copy under the title. */
  subtitle?: ReactNode;
  /** Form / content for the right panel. */
  children: ReactNode;
  /** Cross-link below the form · «Вспомнил? Войти» / etc. */
  crossLink?: ReactNode;
  /**
   * @deprecated · slot_ux_polish UXP.3 (2026-05-23) · the Buddy mascot was
   * removed in the auth-screen polish (Genie pick). The prop is kept so
   * existing callers (Signup / VerifyEmail) still type-check — it is now a
   * no-op. A future cleanup pass can drop it once callers are migrated.
   */
  pulseBuddy?: boolean;
}

export function AuthShell({ title, subtitle, children, crossLink }: AuthShellProps) {
  return (
    <div className="auth-shell">
      <header className="auth-shell__topbar" role="banner">
        <a className="logo" href="/" aria-label="QA Studio">
          <span className="logo-dot">QA</span>
          <span>Studio</span>
        </a>
      </header>
      <div className="auth-shell__split">
        <aside className="auth-shell__hero">
          <p className="auth-shell__hero-eyebrow">— QA Studio · 2026</p>
          <p className="auth-shell__hero-tagline">
            Твой путь в <span className="auth-shell__hero-marker">QA</span>
          </p>
        </aside>
        <main className="auth-shell__form-area" id="main">
          <div className="auth-shell__form-card">
            <h1 className="auth-shell__title">{title}</h1>
            {subtitle && <p className="auth-shell__subtitle">{subtitle}</p>}
            <div className="auth-shell__body">{children}</div>
            {crossLink && <div className="auth-shell__cross-link">{crossLink}</div>}
            <LegalLinks />
          </div>
        </main>
      </div>
    </div>
  );
}
